import isEqual from "lodash/isEqual";

/**
 * Utility functions for TableViewer components.
 * Extracted common procedures such as parsing positions, collision detection, etc.
 */

/**
 * Parses a position string like "[x,y,rot,id][...]" into an array of tuples.
 * @param {string} positionString
 * @returns {Array<[number, number, number, number]>}
 */
export function parsePositions(positionString) {
  if (!positionString) return [];
  return positionString
    .replace(/\]\[/g, "],[")
    .split("],[")
    .map((segment) => segment.replace(/\[|\]/g, "").split(",").map(Number));
}

/**
 * Generates diagonal lines to fill a rectangular area, used for masking backgrounds.
 * @param {number} widthPx - area width in pixels
 * @param {number} heightPx - area height in pixels
 * @param {number} lineSpacing - spacing between lines in px (default: 10)
 * @param {number} angleDeg - angle of lines in degrees (default: 35)
 * @returns {Array<{ x1: number, y1: number, x2: number, y2: number }>}
 */
export function generateDiagonalLines(
  widthPx,
  heightPx,
  lineSpacing = 10,
  angleDeg = 35
) {
  const angle = (angleDeg * Math.PI) / 180;
  const diagLen = Math.sqrt(widthPx * widthPx + heightPx * heightPx);
  const totalLines = Math.ceil((widthPx + heightPx) / lineSpacing) * 2;
  const lines = [];

  for (let i = -totalLines; i <= totalLines; i++) {
    const offset = i * lineSpacing;
    const x1 = offset * Math.cos(angle) - diagLen * Math.sin(angle);
    const y1 = offset * Math.sin(angle) + diagLen * Math.cos(angle);
    const x2 = offset * Math.cos(angle) + diagLen * Math.sin(angle);
    const y2 = offset * Math.sin(angle) - diagLen * Math.cos(angle);
    lines.push({
      x1: x1 + widthPx / 2,
      y1: y1 + heightPx / 2,
      x2: x2 + widthPx / 2,
      y2: y2 + heightPx / 2
    });
  }

  return lines;
}

export function makeInstanceIds(itemId, qty) {
  return Array.from({ length: qty }, (_, i) => `${itemId}_${i}`);
}

/**
 * Flattens a list of items to all processed positions for a given table.
 * @param {Array} items - list of processed item objects
 * @param {number|string} id
 * @returns {Array<{ instanceId: string, x: number, y: number, width: number, height: number, rotation: number }>}
 */
export function getTablePositions(items, id) {
  return items
    .flatMap((item) => item.processedPositions || [])
    .filter((pos) => pos.id == id);
}

/**
 * Finds the first free (non-overlapping) position in a grid for a w x h rectangle.
 * @param {number} w - width of rectangle
 * @param {number} h - height of rectangle
 * @param {string} currentInstanceId - instance being placed
 * @param {number} tableW - table width
 * @param {number} tableH - table height
 * @param {Array} allPositions - existing positions on table
 * @param {number} step - grid step in px (default: 10)
 * @returns {{ x: number, y: number }|null}
 */
export function findFreePosition(
  w,
  h,
  currentInstanceId,
  tableW,
  tableH,
  allPositions,
  step = 10
) {
  for (let yy = 0; yy <= tableH - h; yy += step) {
    for (let xx = 0; xx <= tableW - w; xx += step) {
      const collision = allPositions.some((p) => {
        if (p.instanceId === currentInstanceId) return false;
        return (
          xx < p.x + p.width &&
          xx + w > p.x &&
          yy < p.y + p.height &&
          yy + h > p.y
        );
      });
      if (!collision) return { x: xx, y: yy };
    }
  }
  return null;
}

/**
 * Checks collision with other items and snaps to edges if within a threshold.
 * @param {number} newX
 * @param {number} newY
 * @param {number} w
 * @param {number} h
 * @param {string} currentInstanceId
 * @param {Array} allPoses - positions to check against
 * @param {number} snapThreshold - snapping distance (default: 20)
 * @returns {{ x: number, y: number }} - adjusted coords
 */
export function checkCollisionAndSnap(
  newX,
  newY,
  w,
  h,
  currentInstanceId,
  allPoses,
  snapThreshold = 20
) {
  let adjX = newX;
  let adjY = newY;

  allPoses.forEach((p) => {
    if (p.instanceId === currentInstanceId) return;
    if (
      newX < p.x + p.width &&
      newX + w > p.x &&
      newY < p.y + p.height &&
      newY + h > p.y
    ) {
      const dl = Math.abs(newX + w - p.x);
      const dr = Math.abs(newX - (p.x + p.width));
      const dt = Math.abs(newY + h - p.y);
      const db = Math.abs(newY - (p.y + p.height));

      if (dl < snapThreshold && newY + h > p.y && newY < p.y + p.height)
        adjX = p.x - w;
      else if (dr < snapThreshold && newY + h > p.y && newY < p.y + p.height)
        adjX = p.x + p.width;
      if (dt < snapThreshold && newX + w > p.x && newX < p.x + p.width)
        adjY = p.y - h;
      else if (db < snapThreshold && newX + w > p.x && newX < p.x + p.width)
        adjY = p.y + p.height;
    }
  });

  return { x: adjX, y: adjY };
}

/**
 * Groups tables by their colorId.
 * @param {Array} tables
 * @returns {Record<string, Array>}
 */
export function groupByColor(tables) {
  return tables.reduce((acc, tbl) => {
    const key = tbl.color?.colorId ?? "__NO_COLOR__";
    if (!acc[key]) acc[key] = [];
    acc[key].push(tbl);
    return acc;
  }, {});
}

/* =========================================================
   SAFE CLONE
========================================================= */
export function safeClone(x) {
  try {
    return structuredClone(x);
  } catch {
    return JSON.parse(JSON.stringify(x));
  }
}

/* =========================================================
   NORMALIZE – WORK
========================================================= */
export function normalizeWorkForCompare(w) {
  if (!w) return null;

  return {
    workId: w.workId,
    cutLength: w.cutLength,
    kantLength: w.kantLength,
    partCount: w.partCount,
    measureCount: w.measureCount,
    tableUsage: w.tableUsage,
    usedTables: w.usedTables,
    woodPrice: w.woodPrice,
    clientPrice: w.clientPrice
  };
}

/* =========================================================
   NORMALIZE – TABLE
========================================================= */
export function normalizeTableForCompare(t) {
  if (!t) return null;

  const rawCutOrientation = t.cutOrientation ?? t.cut_orientation ?? false;
  return {
    id: t.id,
    size: t.size,
    colorId: t.color?.colorId ?? null,
    workId: t.work?.workId ?? null,
    cutLength: t.cutLength,
    kantLength: t.kantLength,
    measureCount: t.measureCount,
    partCount: t.partCount,
    tableUsage: t.tableUsage,
    price: t.price,
    trim: Number(t.trim ?? t.TRIM ?? 0),
    righe: t.righe ?? "",
    righeAbs: t.righeAbs ?? t.righe_abs ?? "",
    dati: t.dati ?? "",
    riferimenti: t.riferimenti ?? "",
    cutOrientation:
      rawCutOrientation === true ||
      rawCutOrientation === 1 ||
      rawCutOrientation === "1" ||
      rawCutOrientation === "true",
    split: t.split ?? false
  };
}

/* =========================================================
   BUILD ITEM UPDATE PAYLOAD
========================================================= */
export function buildItemUpdatePayload(updated) {
  const sizeStr =
    typeof updated.size === "string"
      ? updated.size
      : JSON.stringify(updated.size ?? "[0,0]");

  const kantStr =
    typeof updated.kant === "string"
      ? updated.kant
      : `[${updated.cantType ?? "-"},${updated.longCant ?? 0},${
          updated.shortCant ?? 0
        }]`;

  const payload = {
    name: updated.name ?? "",
    qty: Number(updated.qty) || 0,
    size: sizeStr,
    kant: kantStr,
    details: updated.details ?? "",
    material: updated.material ?? "",
    rotable: !!updated.rotable,
    color: updated.color ? { colorId: Number(updated.color.colorId) } : null,
    tablePosition: updated.tablePosition ?? "",
    milling: updated.milling?.millingId
      ? { millingId: Number(updated.milling.millingId) }
      : updated.millingId
        ? { millingId: Number(updated.millingId) }
        : null
  };

  return payload;
}

/* =========================================================
   NORMALIZE ITEM FOR COMPARE
========================================================= */
export function normalizeItemForCompare(item) {
  if (!item) return null;

  return {
    itemId: item.itemId,
    ...buildItemUpdatePayload(item)
  };
}

/* =========================================================
   DIFF CHANGED ITEMS
========================================================= */
export function diffChangedItems(original = [], current = []) {
  const originalById = new Map(
    original.map((item) => [String(item.itemId), item])
  );

  return current.reduce((changedItems, currentItem) => {
    const originalItem = originalById.get(String(currentItem.itemId));

    if (!originalItem) {
      return changedItems;
    }

    const normalizedOriginal = normalizeItemForCompare(originalItem);

    const normalizedCurrent = normalizeItemForCompare(currentItem);

    if (!isEqual(normalizedOriginal, normalizedCurrent)) {
      changedItems.push(currentItem);
    }

    return changedItems;
  }, []);
}

/* =========================================================
   PARSE TABLE POSITION
   "[x,y,rotation,id],[...]"
========================================================= */
export function parseTablePosition(tablePosition) {
  if (!tablePosition) return [];

  return tablePosition
    .replace(/\]\[/g, "],[")
    .split("],[")
    .map((pos) =>
      pos
        .replace(/\[|\]/g, "")
        .split(",")
        .map((v) => parseFloat(v))
    )
    .filter((arr) => arr.length >= 4 && arr.every((v) => !Number.isNaN(v)));
}

/* =========================================================
   REBUILD PROCESSED POSITIONS
========================================================= */
export function rebuildProcessedPositions(item, makeInstanceIdsFn) {
  const positions = parseTablePosition(item.tablePosition);
  const [origH, origW] = JSON.parse(item.size || "[0,0]");
  const instanceIds = makeInstanceIdsFn(item.itemId, item.qty);

  return {
    ...item,
    processedPositions: positions.map(([x, y, rotation, id], idx) => {
      const isHorizontal = rotation === 1;
      const width = isHorizontal ? origH : origW;
      const height = isHorizontal ? origW : origH;

      return {
        instanceId: instanceIds[idx],
        x,
        y,
        rotation,
        id,
        width,
        height
      };
    })
  };
}

/* =========================================================
   PARSE RIGHE (CUT LINES)
========================================================= */
function expandRigheAbsRows(text) {
  return text.split("\n").flatMap((line) => {
    const match = line.match(/^\s*(\d+)\s*=(.*)$/);
    return match
      ? match[2].split("|").map((geometry) => `${match[1]}=${geometry}`)
      : [line];
  });
}

export function parseRighe(table) {
  if (!table?.righeAbs) return { horizontals: [], verticals: [] };

  const [H, W] = JSON.parse(table.size || "[0,0]");
  const rows = expandRigheAbsRows(table.righeAbs)
    .map((s) => s.trim())
    .filter(Boolean);

  const horizontals = [];
  const verticals = [];

  const clamp = (v, max) => Math.max(0, Math.min(max, v));
  const swapXY = Boolean(table.cutOrientation);

  for (const row of rows) {
    const m = row.match(/^\s*(\d+)\s*=\s*(RY|Y|RX|X|U)\s*,\s*([-\d.]+)/i);
    if (!m) continue;

    const idx = Number(m[1]);
    const token = m[2].toUpperCase();
    const value = Number(m[3]);
    if (Number.isNaN(value)) continue;

    const isX = token === "X" || token === "RX";
    const isY = token === "Y" || token === "RY" || token === "U";

    if (!swapXY) {
      if (isX) {
        horizontals.push({
          idx,
          token,
          y: clamp(value, H),
          x1: 0,
          x2: W
        });
      } else if (isY) {
        verticals.push({
          idx,
          token,
          x: clamp(value, W),
          y1: 0,
          y2: H
        });
      }
    } else {
      if (isX) {
        verticals.push({
          idx,
          token,
          x: clamp(value, W),
          y1: 0,
          y2: H
        });
      } else if (isY) {
        horizontals.push({
          idx,
          token,
          y: clamp(value, H),
          x1: 0,
          x2: W
        });
      }
    }
  }

  horizontals.sort((a, b) => a.idx - b.idx);
  verticals.sort((a, b) => a.idx - b.idx);

  return { horizontals, verticals };
}

export function parseRigheAbs(table) {
  const righeAbs = table?.righeAbs ?? table?.righe_abs ?? "";
  if (!righeAbs) return { horizontals: [], verticals: [] };
  const [H, W] = JSON.parse(table.size || "[0,0]");
  const rows = expandRigheAbsRows(righeAbs)
    .map((s) => s.trim())
    .filter(Boolean);

  const horizontals = [];
  const verticals = [];

  const clamp = (v, max) => Math.max(0, Math.min(max, v));
  const rawCutOrientation =
    table.cutOrientation ?? table.cut_orientation ?? false;

  const swapXY =
    rawCutOrientation === true ||
    rawCutOrientation === 1 ||
    rawCutOrientation === "1" ||
    rawCutOrientation === "true";
  for (const row of rows) {
    const m = row.match(
      /^\s*(\d+)\s*=\s*(RY|Y|RX|X|U)\s*,\s*([-\d.]+)\s*,\s*([-\d.]+)\s*,\s*([-\d.]+)/i
    );

    if (!m) continue;

    const idx = Number(m[1]);
    const token = m[2].toUpperCase();
    const abs = Number(m[3]);
    const spanStart = Number(m[4]);
    const spanEnd = Number(m[5]);

    if ([idx, abs, spanStart, spanEnd].some(Number.isNaN)) continue;

    const isX = token === "X" || token === "RX";
    const isY = token === "Y" || token === "RY" || token === "U";

    if (!swapXY) {
      // normál orientáció
      if (isX) {
        horizontals.push({
          idx,
          token,
          y: clamp(abs, H),
          x1: clamp(spanStart, W),
          x2: clamp(spanEnd, W)
        });
      } else if (isY) {
        verticals.push({
          idx,
          token,
          x: clamp(abs, W),
          y1: clamp(spanStart, H),
          y2: clamp(spanEnd, H)
        });
      }
    } else {
      // swap X/Y
      if (isX) {
        verticals.push({
          idx,
          token,
          x: clamp(abs, W),
          y1: clamp(spanStart, H),
          y2: clamp(spanEnd, H)
        });
      } else if (isY) {
        horizontals.push({
          idx,
          token,
          y: clamp(abs, H),
          x1: clamp(spanStart, W),
          x2: clamp(spanEnd, W)
        });
      }
    }
  }

  horizontals.sort((a, b) => a.idx - b.idx);
  verticals.sort((a, b) => a.idx - b.idx);

  return { horizontals, verticals };
}

/* =========================================================
   FORMAT NUMBER
========================================================= */
export function fmtNum(v, decimals = 1) {
  if (v == null || Number.isNaN(Number(v))) return "0";
  const num = Number(v);
  return Number.isInteger(num) ? String(num) : num.toFixed(decimals);
}

/* =========================================================
   PARSE USED TABLES STRING
   "[12:3.5|19:2]"
========================================================= */
export function parseUsedTables(usedTablesStr, colors) {
  if (!usedTablesStr) return [];

  const s = usedTablesStr.replace(/^\[|\]$/g, "").trim();
  if (!s) return [];

  return s.split("|").map((part) => {
    const [cidStr, valStr] = part.split(":").map((x) => x.trim());
    const colorId = Number(cidStr);
    const count = Number(valStr);
    const colorObj = colors?.find((c) => c.colorId === colorId);

    return {
      colorId,
      count,
      name: colorObj?.name ?? `#${colorId}`
    };
  });
}

/* =========================================================
   COMPARE HELPERS
========================================================= */
export function hasTableChanges(originalTables, currentTables) {
  return !isEqual(
    originalTables.map(normalizeTableForCompare),
    currentTables.map(normalizeTableForCompare)
  );
}

export function hasWorkChanges(originalWork, currentWork) {
  return !isEqual(
    normalizeWorkForCompare(originalWork),
    normalizeWorkForCompare(currentWork)
  );
}

export function computeDragResult({
  startX,
  startY,
  origX,
  origY,
  clientX,
  clientY,
  scaleFactor
}) {
  const dx = (clientX - startX) / scaleFactor;
  const dy = (clientY - startY) / scaleFactor;

  return {
    x: origX + dx,
    y: origY + dy
  };
}
export function rebuildProcessed(item, tablePositionParser, makeInstanceIds) {
  const positions = tablePositionParser(item.tablePosition);

  const [origH, origW] = JSON.parse(item.size || "[0,0]");

  const instanceIds = makeInstanceIds(item.itemId, positions.length);

  return {
    ...item,

    processedPositions: positions.map(
      ([x, y, rotation, id, rawDatiRowNumber], idx) => {
        const isHorizontal = rotation === 1;

        const width = isHorizontal ? origH : origW;

        const height = isHorizontal ? origW : origH;

        const datiRowNumber = Number(rawDatiRowNumber);

        return {
          instanceId: instanceIds[idx] ?? `${item.itemId}_${idx}`,

          x,
          y,
          rotation,
          id,
          width,
          height,

          datiRowNumber:
            Number.isInteger(datiRowNumber) && datiRowNumber > 0
              ? datiRowNumber
              : null
        };
      }
    )
  };
}

export function positionsForTable(item, tableId) {
  return (item.processedPositions || []).filter((p) => p.id == tableId);
}

export function toKantList(kantStr) {
  if (!kantStr) return [];

  return kantStr
    .split("|")
    .map((s) => s.trim())
    .filter(Boolean)
    .filter((row) => {
      const code = row.split(":")[0]?.trim();
      return code !== "-";
    });
}

export function resolveColorName(table, colors) {
  const cid = table?.color?.colorId ?? null;
  const colorObj = colors.find((c) => c.colorId === cid);
  return colorObj?.name ?? table?.colorName ?? table?.color?.name ?? "";
}
export function buildProcessedItemsFromBackend(
  items,
  parsePositions,
  makeInstanceIds
) {
  return items.map((item) => {
    const positions = parsePositions(item.tablePosition);

    const [origH, origW] = JSON.parse(item.size || "[0,0]");

    const instanceIds = makeInstanceIds(item.itemId, positions.length);

    return {
      ...item,

      processedPositions: positions.map(
        ([x, y, rotation, id, rawDatiRowNumber], idx) => {
          const isHorizontal = rotation === 1;

          const width = isHorizontal ? origH : origW;

          const height = isHorizontal ? origW : origH;

          const datiRowNumber = Number(rawDatiRowNumber);

          return {
            instanceId: instanceIds[idx] ?? `${item.itemId}_${idx}`,

            x,
            y,
            rotation,
            id,
            width,
            height,

            datiRowNumber:
              Number.isInteger(datiRowNumber) && datiRowNumber > 0
                ? datiRowNumber
                : null
          };
        }
      )
    };
  });
}

export function rewriteItemTableIds(items, idMap, parsePositions) {
  return items.map((item) => {
    if (!item.tablePosition) return item;

    const positions = parsePositions(item.tablePosition);

    const rewritten = positions.map(([x, y, rotation, id, datiRowNumber]) => [
      x,
      y,
      rotation,
      idMap[id] !== undefined ? idMap[id] : id,
      datiRowNumber
    ]);

    const newTablePosition = rewritten
      .map(
        ([x, y, rotation, id, datiRowNumber]) =>
          `[${x},${y},${rotation},${id},${
            Number(datiRowNumber) > 0 ? Number(datiRowNumber) : 0
          }]`
      )
      .join(",");

    const processedPositions = (item.processedPositions || []).map((p) => ({
      ...p,
      id: idMap[p.id] !== undefined ? idMap[p.id] : p.id
    }));

    return {
      ...item,
      tablePosition: newTablePosition,
      processedPositions
    };
  });
}

export function mergeGeneratedTablesWithExisting(
  existingTables,
  generatedTables
) {
  const mergedTables = [];
  const existingByColor = {};

  existingTables.forEach((t) => {
    const cid = t.color?.colorId;
    if (!existingByColor[cid]) existingByColor[cid] = [];
    existingByColor[cid].push(t);
  });

  generatedTables.forEach((genTable) => {
    const cid = genTable.color?.colorId;
    const candidates = existingByColor[cid] || [];

    const matchIndex = candidates.findIndex(
      (ex) =>
        ex.size === genTable.size &&
        ex.partCount === genTable.partCount &&
        ex.cutLength === genTable.cutLength
    );

    if (matchIndex >= 0) {
      const matched = candidates.splice(matchIndex, 1)[0];
      mergedTables.push({
        ...genTable,
        id: matched.id,
        frontend: false
      });
    } else {
      mergedTables.push(genTable);
    }
  });

  return mergedTables;
}

export function computeScaleFactor(containerWidth, containerHeight, tableSize) {
  const [height = 1, width = 1] = JSON.parse(tableSize || "[1,1]");

  const scaleW = containerWidth / width;
  const scaleH = containerHeight / height;

  return Math.min(scaleW, scaleH) * 0.75;
}

export function extractTableMetrics(table, colors) {
  if (!table) return null;

  const cid = table.color?.colorId ?? null;
  const colorObj = colors.find((c) => c.colorId === cid);

  return {
    id: table.id ?? table.tableId,
    colorName: colorObj?.name ?? "",
    usage: Number(table.tableUsage ?? table.table_usage ?? 0),
    cutLength: Number(table.cutLength ?? table.cut_length ?? 0),
    partCount: Number(table.partCount ?? table.part_count ?? 0),
    measureCount: Number(table.measureCount ?? table.measure_count ?? 0),
    kantLength: table.kantLength ?? table.kant_length ?? "",
    price: Number(table.price ?? 0)
  };
}

/**
 * Computes the rectangular waste pieces of the current table.
 *
 * The table is partitioned in the original cut order using righeAbs. Kerf is
 * removed from the positive side of every cut. A final partition is waste when
 * it does not contain/intersect a created item on the current table.
 *
 * Returned coordinates use the same millimetre coordinate system as
 * processedPositions, therefore they can be passed directly to SVG <rect>
 * after applying the viewer scale factor.
 *
 * @param {Array} createdItems items containing processedPositions
 * @param {Object} table current created table
 * @param {Object} options
 * @param {number} options.kerf saw kerf in mm
 * @param {number} options.trim fallback trim when table.trim is missing
 * @param {number} options.tolerance geometry tolerance in mm
 * @param {number} options.minWidth minimum returned waste width in mm
 * @param {number} options.minHeight minimum returned waste height in mm
 * @param {number} options.minArea minimum returned waste area in mm²
 * @returns {Array<{
 *   instanceId: string,
 *   id: number|string,
 *   rotation: 0,
 *   isWaste: true,
 *   x: number,
 *   y: number,
 *   width: number,
 *   height: number,
 *   area: number,
 *   sourceCutIndexes: number[]
 * }>}
 */
export function computeWasteRects(
  createdItems,
  table,
  {
    kerf = 4.4,
    trim: fallbackTrim = 0,
    outerWidth: requestedOuterWidth,
    outerHeight: requestedOuterHeight,
    tolerance = 0.25,
    minWidth = 1,
    minHeight = 1,
    minArea = 1,
    debugWaste = true
  } = {}
) {
  if (!table) return [];
  const wasteLog = (...args) => {
    if (debugWaste) {
      console.log("[WASTE]", ...args);
    }
  };
  // =========================================================
  // TÁBLAMÉRET
  // =========================================================

  let parsedSize;

  try {
    parsedSize = Array.isArray(table.size)
      ? table.size
      : JSON.parse(table.size || "[0,0]");
  } catch {
    return [];
  }

  const tableH = Number(parsedSize?.[0]);
  const tableW = Number(parsedSize?.[1]);

  if (!(tableW > 0) || !(tableH > 0)) {
    return [];
  }

  const safeKerf = Math.max(0, Number(kerf) || 0);

  const trim = Math.max(
    0,
    Number(table.trim ?? table.TRIM ?? fallbackTrim) || 0
  );

  const displayRight = Math.max(tableW, Number(requestedOuterWidth) || tableW);

  const displayBottom = Math.max(
    tableH,
    Number(requestedOuterHeight) || tableH
  );
  /* wasteLog("TABLE SETTINGS", {
    tableId: table.id,
    rawSize: table.size,

    tableW,
    tableH,

    trim,
    kerf: safeKerf,
    tolerance,

    requestedOuterWidth,
    requestedOuterHeight,

    displayRight,
    displayBottom,

    minWidth,
    minHeight,
    minArea
  });*/
  const clamp = (value, min, max) => Math.max(min, Math.min(max, value));

  const rightOf = (rect) => rect.x + rect.width;
  const bottomOf = (rect) => rect.y + rect.height;

  // =========================================================
  // TÁBLÁN LÉVŐ ELEMEK
  //
  // Ezek nem alakítják ki a waste geometriáját.
  // Csak azt döntik el, hogy egy righeAbs által létrehozott
  // végső régió foglalt vagy hulladék.
  // =========================================================

  const tableItems = (Array.isArray(createdItems) ? createdItems : []).flatMap(
    (item) => {
      let originalH = 0;
      let originalW = 0;

      try {
        const size = Array.isArray(item?.size)
          ? item.size
          : JSON.parse(item?.size || "[0,0]");

        originalH = Number(size?.[0]) || 0;
        originalW = Number(size?.[1]) || 0;
      } catch {
        // A processedPositions normál esetben tartalmaz méretet.
      }

      return (item?.processedPositions || [])
        .filter((position) => String(position?.id) === String(table.id))
        .map((position) => {
          const rotated = Number(position.rotation) === 1;

          const width =
            Number(position.width) || (rotated ? originalH : originalW) || 0;

          const height =
            Number(position.height) || (rotated ? originalW : originalH) || 0;

          const x = clamp(Number(position.x) || 0, 0, tableW);

          const y = clamp(Number(position.y) || 0, 0, tableH);

          return {
            itemId: item.itemId,
            instanceId: position.instanceId,
            x,
            y,
            width: Math.max(0, Math.min(width, tableW - x)),
            height: Math.max(0, Math.min(height, tableH - y))
          };
        })
        .filter((rect) => rect.width > tolerance && rect.height > tolerance);
    }
  );
  //wasteLog("OCCUPIED ITEMS", tableItems);
  // =========================================================
  // RIGHEABS VÁGÁSOK
  //
  // Az U* és V* sorok marker sorok, nem geometriai vágások.
  // A parseRigheAbs ezért helyesen nem adja vissza őket.
  //
  // A tényleges X/Y/RX/RY sorok idx szerinti eredeti
  // sorrendben kerülnek feldolgozásra.
  // =========================================================

  const { horizontals, verticals } = parseRigheAbs(table);

  const cuts = [
    ...horizontals.map((line) => ({
      idx: Number(line.idx),
      axis: "horizontal",
      position: Number(line.y),
      spanStart: Math.min(Number(line.x1), Number(line.x2)),
      spanEnd: Math.max(Number(line.x1), Number(line.x2)),
      token: line.token
    })),

    ...verticals.map((line) => ({
      idx: Number(line.idx),
      axis: "vertical",
      position: Number(line.x),
      spanStart: Math.min(Number(line.y1), Number(line.y2)),
      spanEnd: Math.max(Number(line.y1), Number(line.y2)),
      token: line.token
    }))
  ]
    .filter(
      (cut) =>
        Number.isInteger(cut.idx) &&
        Number.isFinite(cut.position) &&
        Number.isFinite(cut.spanStart) &&
        Number.isFinite(cut.spanEnd)
    )
    .sort((a, b) => a.idx - b.idx);

  /*
   * Szigorúan righeAbs-alapú működés.
   *
   * Ha nincs geometriai vágás, nem próbálunk az elemek
   * kivonásából mesterséges waste téglalapokat előállítani.
   */
  if (cuts.length === 0) {
    return [];
  }

  // =========================================================
  // KIINDULÓ VÁGHATÓ RÉGIÓ
  // =========================================================

  const rootX = clamp(trim, 0, tableW);
  const rootY = clamp(trim, 0, tableH);

  const rootWidth = tableW - rootX;
  const rootHeight = tableH - rootY;

  if (rootWidth <= tolerance || rootHeight <= tolerance) {
    return [];
  }

  let regions = [
    {
      x: rootX,
      y: rootY,
      width: rootWidth,
      height: rootHeight,
      sourceCutIndexes: []
    }
  ];

  // =========================================================
  // EGY RÉGIÓ SZÉTVÁGÁSA EGY RIGHEABS VÁGÁSSAL
  // =========================================================

  const splitRegionByCut = (region, cut) => {
    const regionRight = rightOf(region);
    const regionBottom = bottomOf(region);

    const sourceIndexes = Array.isArray(region.sourceCutIndexes)
      ? region.sourceCutIndexes
      : [];

    const withCurrentCut = [...new Set([...sourceIndexes, cut.idx])];

    const result = [];

    const pushRegion = ({ x, y, width, height, affectedByCut = false }) => {
      if (
        !Number.isFinite(x) ||
        !Number.isFinite(y) ||
        !Number.isFinite(width) ||
        !Number.isFinite(height) ||
        width <= tolerance ||
        height <= tolerance
      ) {
        return;
      }

      result.push({
        x,
        y,
        width,
        height,
        sourceCutIndexes: affectedByCut ? withCurrentCut : [...sourceIndexes]
      });
    };

    // =======================================================
    // VÍZSZINTES, RÉSZLEGES VÁGÁS
    // =======================================================

    if (cut.axis === "horizontal") {
      const cutIsInsideRegion =
        cut.position > region.y + tolerance &&
        cut.position < regionBottom - tolerance;

      if (!cutIsInsideRegion) {
        return [region];
      }

      /*
       * A vágási szakasz és a régió vízszintes
       * metszetének meghatározása.
       */
      const overlapStart = Math.max(region.x, cut.spanStart);

      const overlapEnd = Math.min(regionRight, cut.spanEnd);

      if (overlapEnd <= overlapStart + tolerance) {
        return [region];
      }

      /*
       * Bal oldali rész, amelyet a részleges vágás
       * nem érint.
       */
      pushRegion({
        x: region.x,
        y: region.y,
        width: overlapStart - region.x,
        height: region.height
      });

      /*
       * A vágási szakasz fölötti rész.
       */
      pushRegion({
        x: overlapStart,
        y: region.y,
        width: overlapEnd - overlapStart,
        height: cut.position - region.y,
        affectedByCut: true
      });

      /*
       * A kerf a vágás pozitív, alsó oldalán van.
       */
      const lowerY = Math.min(regionBottom, cut.position + safeKerf);

      /*
       * A vágási szakasz alatti rész.
       */
      pushRegion({
        x: overlapStart,
        y: lowerY,
        width: overlapEnd - overlapStart,
        height: regionBottom - lowerY,
        affectedByCut: true
      });

      /*
       * Jobb oldali rész, amelyet a részleges vágás
       * nem érint.
       */
      pushRegion({
        x: overlapEnd,
        y: region.y,
        width: regionRight - overlapEnd,
        height: region.height
      });

      return result.length > 0 ? result : [region];
    }

    // =======================================================
    // FÜGGŐLEGES, RÉSZLEGES VÁGÁS
    // =======================================================

    const cutIsInsideRegion =
      cut.position > region.x + tolerance &&
      cut.position < regionRight - tolerance;

    if (!cutIsInsideRegion) {
      return [region];
    }

    /*
     * A vágási szakasz és a régió függőleges
     * metszetének meghatározása.
     */
    const overlapStart = Math.max(region.y, cut.spanStart);

    const overlapEnd = Math.min(regionBottom, cut.spanEnd);

    if (overlapEnd <= overlapStart + tolerance) {
      return [region];
    }

    /*
     * Felső rész, amelyet a részleges vágás
     * nem érint.
     */
    pushRegion({
      x: region.x,
      y: region.y,
      width: region.width,
      height: overlapStart - region.y
    });

    /*
     * A vágási szakasz bal oldali része.
     */
    pushRegion({
      x: region.x,
      y: overlapStart,
      width: cut.position - region.x,
      height: overlapEnd - overlapStart,
      affectedByCut: true
    });

    /*
     * A kerf a vágás pozitív, jobb oldalán van.
     */
    const rightX = Math.min(regionRight, cut.position + safeKerf);

    /*
     * A vágási szakasz jobb oldali része.
     */
    pushRegion({
      x: rightX,
      y: overlapStart,
      width: regionRight - rightX,
      height: overlapEnd - overlapStart,
      affectedByCut: true
    });

    /*
     * Alsó rész, amelyet a részleges vágás
     * nem érint.
     */
    pushRegion({
      x: region.x,
      y: overlapEnd,
      width: region.width,
      height: regionBottom - overlapEnd
    });

    return result.length > 0 ? result : [region];
  };

  // =========================================================
  // RIGHEABS SORREND SZERINTI PARTÍCIONÁLÁS
  // =========================================================

  for (const cut of cuts) {
    const regionsBeforeCut = regions.map((region) => ({
      ...region,
      sourceCutIndexes: [...(region.sourceCutIndexes || [])]
    }));

    regions = regions.flatMap((region) => splitRegionByCut(region, cut));

    if (debugWaste) {
      /*  console.groupCollapsed(
        `[WASTE] CUT #${cut.idx} ${cut.axis} @ ${cut.position}`
      );

      console.log("Cut:", cut);

      console.log("Regions BEFORE:", regionsBeforeCut);

      console.log("Regions AFTER:", regions);

      console.groupEnd();*/
    }
  }

  // =========================================================
  // FOGLALTSÁG VIZSGÁLATA
  //
  // A régió geometriáját kizárólag a righeAbs adta.
  // Az elemek csak a régió waste / occupied státuszát adják.
  // =========================================================

  /*
   * Egy foglalt elem kivonása egy righeAbs alapján
   * létrejött régióból.
   *
   * A righeAbs-partícionálással egységesen a kerf az elem
   * pozitív (jobb és alsó) oldalán kerül eltávolításra.
   * A tolerance kizárólag geometriai összehasonlításra szolgál,
   * nem növeli vagy csökkenti a visszaadott waste méretét.
   *
   * Az eredmény legfeljebb négy, egymást nem fedő téglalap:
   * felső, bal, jobb és alsó. Az alsó darab csak az elem
   * anyagszélességét örökli, ezért a jobb oldali kerf nem jelenik
   * meg újra hulladékként az elem alatt.
   */
  const subtractOccupiedRect = (freeRect, occupiedRect) => {
    const freeRight = rightOf(freeRect);
    const freeBottom = bottomOf(freeRect);

    const occupiedLeft = clamp(occupiedRect.x, 0, tableW);
    const occupiedTop = clamp(occupiedRect.y, 0, tableH);
    const occupiedRight = clamp(rightOf(occupiedRect), 0, tableW);
    const occupiedBottom = clamp(bottomOf(occupiedRect), 0, tableH);

    const intersectionLeft = Math.max(freeRect.x, occupiedLeft);
    const intersectionTop = Math.max(freeRect.y, occupiedTop);
    const intersectionRight = Math.min(freeRight, occupiedRight);
    const intersectionBottom = Math.min(freeBottom, occupiedBottom);

    const hasIntersection =
      intersectionRight > intersectionLeft + tolerance &&
      intersectionBottom > intersectionTop + tolerance;

    if (!hasIntersection) {
      return [freeRect];
    }

    /*
     * Ugyanaz a pozitív oldali kerf-konvenció, mint a
     * splitRegionByCut() lowerY/rightX számításánál.
     */
    const kerfRight = Math.min(
      freeRight,
      clamp(occupiedRight + safeKerf, 0, tableW)
    );

    const kerfBottom = Math.min(
      freeBottom,
      clamp(occupiedBottom + safeKerf, 0, tableH)
    );

    const result = [];

    const pushFragment = ({ x, y, width, height }) => {
      if (width <= tolerance || height <= tolerance) {
        return;
      }

      result.push({
        ...freeRect,
        x,
        y,
        width,
        height,

        /*
         * Megtartjuk, mely righeAbs vágásokból
         * származott az eredeti partíció.
         */
        sourceCutIndexes: [...(freeRect.sourceCutIndexes || [])]
      });
    };

    // Felső szabad rész: a teljes régió az elem felett.
    pushFragment({
      x: freeRect.x,
      y: freeRect.y,
      width: freeRect.width,
      height: intersectionTop - freeRect.y
    });

    /*
     * Bal és jobb oldali szabad részek. A jobb oldali fragmentum
     * csak a teljes kerf után kezdődhet.
     */
    pushFragment({
      x: freeRect.x,
      y: intersectionTop,
      width: intersectionLeft - freeRect.x,
      height: freeBottom - intersectionTop
    });

    pushFragment({
      x: kerfRight,
      y: intersectionTop,
      width: freeRight - kerfRight,
      height: freeBottom - intersectionTop
    });

    /*
     * Alsó szabad rész: a teljes alsó kerf után kezdődik, és csak
     * az elem tényleges anyagszélességéig tart. Így a jobb oldali
     * kerf nem számolódik bele még egyszer a waste szélességébe.
     */
    pushFragment({
      x: intersectionLeft,
      y: kerfBottom,
      width: intersectionRight - intersectionLeft,
      height: freeBottom - kerfBottom
    });

    return result;
  };

  /*
   * Minden righeAbs-partícióból kivonjuk az összes
   * ténylegesen elhelyezett elemet.
   *
   * A megmaradó téglalapok a hulladékok.
   */
  let wasteRegions = regions.flatMap((region, regionIndex) => {
    let freeFragments = [region];

    if (debugWaste) {
      /*  console.groupCollapsed(
        `[WASTE] REGION ${regionIndex} occupied subtraction`
      );
      */
      // console.log("Original righeAbs region:", region);
    }

    for (const itemRect of tableItems) {
      const before = freeFragments.map((fragment) => ({
        ...fragment,
        sourceCutIndexes: [...(fragment.sourceCutIndexes || [])]
      }));

      freeFragments = freeFragments.flatMap((fragment) =>
        subtractOccupiedRect(fragment, itemRect)
      );

      if (debugWaste && !isEqual(before, freeFragments)) {
        /*   console.log("Occupied item affected region:", {
          item: itemRect,
          before,
          after: freeFragments
        });*/
      }

      if (freeFragments.length === 0) {
        break;
      }
    }

    if (debugWaste) {
      // console.log("Final free fragments:", freeFragments);
      // console.groupEnd();
    }

    return freeFragments;
  });

  // =========================================================
  // KIMENET
  // =========================================================

  return wasteRegions
    .map((region, regionIndex) => {
      const regionRight = rightOf(region);
      const regionBottom = bottomOf(region);

      const edgeTolerance = Math.max(tolerance, 0.5);

      const touchesRight = Math.abs(regionRight - tableW) <= edgeTolerance;

      const touchesBottom = Math.abs(regionBottom - tableH) <= edgeTolerance;

      const right = touchesRight ? displayRight : regionRight;

      const bottom = touchesBottom ? displayBottom : regionBottom;

      const calculatedWidth = Math.max(0, right - region.x);

      const calculatedHeight = Math.max(0, bottom - region.y);
      /*
      wasteLog("WASTE SIZE CALCULATION", {
        regionIndex,

        // honnan jött ez a régió
        sourceCutIndexes: region.sourceCutIndexes,

        // eredeti régió
        region: {
          x: region.x,
          y: region.y,
          width: region.width,
          height: region.height
        },

        // eredeti jobb/alsó széle
        regionRight,
        regionBottom,

        // tábla
        tableW,
        tableH,

        // külső méret
        displayRight,
        displayBottom,

        // hozzáér-e a tábla széléhez
        touchesRight,
        touchesBottom,

        // melyik koordinátát használta
        selectedRight: right,
        selectedBottom: bottom,

        // VÉGEREDMÉNY
        calculatedWidth,
        calculatedHeight,

        reason: {
          width: touchesRight
            ? "table right edge -> outerWidth/displayRight used"
            : "internal region -> righeAbs regionRight used",

          height: touchesBottom
            ? "table bottom edge -> outerHeight/displayBottom used"
            : "internal region -> righeAbs regionBottom used"
        }
      });*/

      return {
        ...region,
        width: calculatedWidth,
        height: calculatedHeight
      };
    })
    .filter(
      (region) =>
        region.width >= minWidth &&
        region.height >= minHeight &&
        region.width * region.height >= minArea
    )
    .sort((a, b) => a.y - b.y || a.x - b.x)
    .map((region, index) => {
      const round = (value) => Number(Number(value).toFixed(4));

      // A kerf-et a régiók felosztása már figyelembe vette.
      // A waste pozícióját nem szabad újra fél kerffel eltolni,
      // mert a kézi elhelyezés ezt a koordinátát használja.
      const x = round(region.x);
      const y = round(region.y);
      const width = round(region.width);
      const height = round(region.height);

      return {
        instanceId: `waste-${table.id ?? "table"}-${index}`,

        id: table.id,
        rotation: 0,
        isWaste: true,

        x,
        y,
        width,
        height,

        area: round(width * height),

        sourceCutIndexes: [...new Set(region.sourceCutIndexes || [])].sort(
          (a, b) => a - b
        )
      };
    });
}
